// src/components/compliance/modals/ClosedTasksModal.jsx

import React from "react";
import {
  buildControlLabel,
  cleanValue,
  formatTimestamp,
  getTaskStatusLabel,
  normalizeText,
} from "../../../utils/complianceFormatters";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

function getTaskTitle(task = {}) {
  return (
    cleanValue(task.title, "") ||
    cleanValue(task.task_title, "") ||
    `Closed ${buildControlLabel(task.control_id || task.controlId, task.rule_id || task.ruleId)}`
  );
}

function getTaskOwner(task = {}) {
  return cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "Unassigned");
}

function getTaskResource(task = {}) {
  return cleanValue(task.resource_id || task.resourceId, "—");
}

function getClosedAt(task = {}) {
  return (
    task.closed_at ||
    task.closedAt ||
    task.closed_on ||
    task.closedOn ||
    task.updated_at ||
    task.updatedAt ||
    task.validated_at ||
    task.validatedAt ||
    null
  );
}

function isApprovedException(task = {}) {
  const validationStatus = normalizeText(task.validation_status || task.validationStatus);
  const metadata = task.task_metadata || task.metadata || {};
  const lastValidation = metadata.last_validation || metadata.lastValidation || {};
  const lastStatus = normalizeText(
    lastValidation.status ||
      lastValidation.validation_status ||
      lastValidation.result ||
      lastValidation.validation_result ||
      ""
  );
  const lastMetadata = lastValidation.metadata || {};
  const decisionPath = normalizeText(lastMetadata.decision_path || "");
  const governance = normalizeText(
    lastMetadata.validation_governance || lastMetadata.governance || lastMetadata.exception_type || ""
  );

  return (
    validationStatus === "approved_exception" ||
    lastStatus === "approved_exception" ||
    governance === "approved_exception" ||
    decisionPath.includes("approved_exception") ||
    decisionPath.includes("retain_as_approved_exception")
  );
}

function isValidatedOrPassed(task = {}) {
  const status = normalizeText(task.status || task.task_status);
  const validationStatus = normalizeText(task.validation_status || task.validationStatus);
  return status === "validated" || validationStatus === "passed" || validationStatus === "pass";
}

function isRecentlyClosed(task = {}) {
  const closedAt = getClosedAt(task);
  if (!closedAt) return false;
  const parsed = new Date(closedAt).getTime();
  if (!Number.isFinite(parsed)) return false;
  return Date.now() - parsed <= 7 * 24 * 60 * 60 * 1000;
}

function getClosureReason(task = {}) {
  return cleanValue(
    task.closure_reason ||
      task.close_reason ||
      task.closed_reason ||
      task.resolution_summary ||
      task.task_metadata?.closure_reason ||
      task.metadata?.closure_reason,
    "No closure reason recorded."
  );
}

function buildDriftParams(task = {}, framework) {
  return {
    framework: task.framework || framework,
    controlId: task.control_id || task.controlId || undefined,
    ruleId: task.rule_id || task.ruleId || undefined,
    resourceId: task.resource_id || task.resourceId || undefined,
  };
}

export default function ClosedTasksModal({
  open,
  framework,
  closedTasks = [],
  totalCount = 0,
  loading = false,
  error = "",
  onClose,
  onReviewTask,
  onViewGuidance,
  onOpenDrift,
}) {
  if (!open) return null;

  const rows = Array.isArray(closedTasks) ? closedTasks : [];
  const shownCount = rows.length;
  const countLabel =
    totalCount > shownCount ? `${shownCount} shown of ${totalCount}` : `${shownCount} shown`;

  const validatedCount = rows.filter(isValidatedOrPassed).length;
  const approvedExceptionCount = rows.filter(isApprovedException).length;
  const recentlyClosedCount = rows.filter(isRecentlyClosed).length;

  return (
    <ModalShell
      open={open}
      ariaLabel="Closed remediation audit records"
      eyebrow="OASYS · CLOSED TASKS"
      title="Closed Remediation Audit Records"
      subtitle="Review completed remediation work, closure rationale, validation outcome, and audit context without expanding the main operations page."
      onClose={onClose}
      maxWidth="1120px"
      accentBorder="rgba(52, 211, 153, 0.45)"
    >
      <div style={metricGridStyle}>
        <MetricTile label="Closed Tasks" value={totalCount || shownCount} sub={`${framework} audit records`} />
        <MetricTile
          label="Validated / Passed"
          value={validatedCount}
          sub="validated closure"
          color={validatedCount > 0 ? "#34d399" : "#fbbf24"}
        />
        <MetricTile
          label="Approved Exceptions"
          value={approvedExceptionCount}
          sub="accepted risk closure"
          color={approvedExceptionCount > 0 ? "#fbbf24" : "#34d399"}
        />
        <MetricTile
          label="Recently Closed"
          value={recentlyClosedCount}
          sub="last 7 days"
          color={recentlyClosedCount > 0 ? "#60a5fa" : "#94a3b8"}
        />
      </div>

      {error ? (
        <div
          style={{
            border: "1px solid rgba(248, 113, 113, 0.38)",
            background: "rgba(127, 29, 29, 0.18)",
            color: "#fecaca",
            borderRadius: 14,
            padding: 12,
            fontSize: 13,
            lineHeight: 1.5,
            fontWeight: 750,
            justifySelf: "start",
          }}
        >
          {error}
        </div>
      ) : null}

      <ModalSection
        title="Closed Task History"
        description="Audit-focused history for closed remediation tasks. Use Review / Manage to inspect the full task trail or Guidance for read-only context."
        badge={loading ? "loading…" : countLabel}
        badgeTone="ok"
      >
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>
            Loading closed remediation records…
          </div>
        ) : rows.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No closed remediation tasks are currently loaded for {framework}.
          </div>
        ) : (
          rows.map((task, index) => {
            const controlId = task.control_id || task.controlId || "";
            const ruleId = task.rule_id || task.ruleId || "";
            const statusLabel = getTaskStatusLabel(task.status || task.task_status || "closed");
            const validationLabel = cleanValue(task.validation_status || task.validationStatus, "pending").replace(/_/g, " ");
            const ownerLabel = getTaskOwner(task);
            const closedAt = getClosedAt(task);
            const closureReason = getClosureReason(task);
            const taskKey = task.id || `${controlId}-${ruleId}-${task.resource_id || task.resourceId || index}`;
            const approvedException = isApprovedException(task);

            return (
              <TaskQueueCard
                key={taskKey}
                title={getTaskTitle(task)}
                dueLabel={closedAt ? formatTimestamp(closedAt) : "Closed date unavailable"}
                chips={[
                  { label: statusLabel, tone: "ok" },
                  { label: approvedException ? "Approved Exception" : validationLabel, tone: approvedException ? "warn" : "ok" },
                  { label: ownerLabel, tone: ownerLabel === "Unassigned" ? "warn" : "default" },
                ]}
                meta={[
                  { label: "Control", value: cleanValue(controlId, "—") },
                  { label: "Rule", value: cleanValue(ruleId, "—") },
                  { label: "Resource", value: getTaskResource(task), wrap: true },
                ]}
                context={closureReason}
                contextLabel="Closure reason"
                actions={[
                  {
                    key: "review",
                    label: "Review / Manage",
                    onClick: () => onReviewTask(task, { returnTo: "closed_tasks" }),
                  },
                  {
                    key: "guidance",
                    label: "Guidance",
                    onClick: () => onViewGuidance(task, { returnTo: "closed_tasks" }),
                  },
                  {
                    key: "drift",
                    label: "Open in Drift",
                    onClick: () => onOpenDrift(buildDriftParams(task, framework)),
                  },
                ]}
              />
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
